import React, { useMemo } from "react";
import { FormControlledCheckboxGroup } from "ui/molecules/form/FormControlledCheckboxGroup";
import { FormControlledRadioGroup } from "ui/molecules/form/FormControlledRadioGroup";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import {
  IMAGE_TYPES,
  QuestionTypeEnum,
} from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { useAssessmentQuestionContext } from "../context/AssessmentQuestionContext";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { Controller } from "react-hook-form";
import UploadDocumentArea from "ui/organisms/UploadDocumentArea";
import {
  generalAllowedExtensions,
  imageAllowedExtensions,
} from "app/constants/allowedExtensionsConstants";
import { FormControlledSignaturePad } from "ui/organisms/FormControlledSignaturePad";

export function AssessmentQuestionField() {
  const {
    type,
    id,
    name,
    otherTextName,
    filesName,
    isSingleFile,
    control,
    isRequired,
    isReadOnly,
    options,
    inputType,
    rules,
    question,
  } = useAssessmentQuestionContext();

  const field = useMemo(() => {
    switch (type) {
      case QuestionTypeEnum.MULTI_SELECT:
        return (
          <FormControlledCheckboxGroup
            id={id}
            name={name}
            otherTextName={otherTextName}
            control={control}
            isRequired={isRequired}
            options={options}
            isReadOnly={isReadOnly}
          />
        );
      case QuestionTypeEnum.RADIO_BUTTONS:
        return (
          <FormControlledRadioGroup
            id={id}
            name={name}
            otherTextName={otherTextName}
            control={control}
            isRequired={isRequired}
            options={options}
            isReadOnly={isReadOnly}
          />
        );
      case QuestionTypeEnum.TEXT_AREA:
        return (
          <FormControlledInputTextArea
            id={id}
            name={name}
            control={control}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
          />
        );
      case QuestionTypeEnum.DATE:
        return (
          <FormControlledDatePicker
            id={id}
            name={name}
            control={control}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
          />
        );
      case QuestionTypeEnum.SINGLE_IMAGE:
      case QuestionTypeEnum.MULTIPLE_IMAGES:
      case QuestionTypeEnum.SINGLE_FILE:
      case QuestionTypeEnum.MULTIPLE_FILES:
        return (
          <Controller
            control={control}
            name={filesName}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <UploadDocumentArea
                value={value}
                onChange={onChange}
                errorMessage={error}
                allowedExtensions={
                  IMAGE_TYPES.includes(type)
                    ? imageAllowedExtensions
                    : generalAllowedExtensions
                }
                singleFile={isSingleFile}
              />
            )}
          ></Controller>
        );
      case QuestionTypeEnum.ELECTRONIC_SIGNATURE:
        return (
          <FormControlledSignaturePad
            id={id}
            name={filesName}
            control={control}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
            label={question?.content}
          />
        );
      default:
        return (
          <FormControlledInput
            id={id}
            name={name}
            control={control}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
            type={inputType}
            rules={rules}
          />
        );
    }
  }, [
    type,
    id,
    name,
    otherTextName,
    control,
    isRequired,
    isReadOnly,
    options,
    inputType,
    rules,
  ]);

  return field;
}
